import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SmallGame from "../../components/SmallGame";
import { Team, Player, Game } from "../../types";

const UserTeam = () => {
    const { id } = useParams<{ id: string }>();
    const [team, setTeam] = useState<Team | undefined>();
    const [players, setPlayers] = useState<Player[]>([]);
    const [keepers, setKeepers] = useState<Player[]>([]);
    const [games, setGames] = useState<Game[]>([]);

    useEffect(() => {
        const getTeam = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_PATH}/api/teams/page/${id}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                const json: { team: Team[], players: Player[], games: Game[] } = await response.json();

                if (response.ok) {
                    if (json.team.length > 0) {
                        setTeam(json.team[0]);
                    }

                    // Filter and sort players
                    const temp_p = json.players.filter(x => 
                        (x.penalties || 0) + (x.goals || 0) + (x.assists || 0) > 0 || x.role !== 'gk'
                    );
                    temp_p.sort((a, b) => {
                        const aPoints = (a.goals || 0) + (a.assists || 0);
                        const bPoints = (b.goals || 0) + (b.assists || 0);
                        if (bPoints !== aPoints) return bPoints - aPoints;
                        if ((b.goals || 0) !== (a.goals || 0)) return (b.goals || 0) - (a.goals || 0);
                        return (a.penalties || 0) - (b.penalties || 0);
                    });
                    setPlayers(temp_p);

                    // Filter and sort keepers
                    const temp_k = json.players.filter(x => 
                        (x.interventions || 0) + (x.recieved_goals || 0) > 0 || x.role === 'gk'
                    );
                    temp_k.sort((a, b) => {
                        const aSavePercentage = (a.recieved_goals || 0) > 0 
                            ? Math.round((100 - ((a.recieved_goals || 0) / ((a.interventions || 0)) * 100)) * 100) / 100
                            : 100;
                        const bSavePercentage = (b.recieved_goals || 0) > 0
                            ? Math.round((100 - ((b.recieved_goals || 0) / ((b.interventions || 0)) * 100)) * 100) / 100
                            : 100;
                        
                        if (bSavePercentage !== aSavePercentage) return bSavePercentage - aSavePercentage;
                        if ((b.recieved_goals || 0) !== (a.recieved_goals || 0)) return (a.recieved_goals || 0) - (b.recieved_goals || 0);
                        return (b.interventions || 0) - (a.interventions || 0);
                    });
                    setKeepers(temp_k);

                    // Sort games - live first, then by status
                    const liveGames = json.games.filter(x => x.status === "live");
                    const otherGames = json.games.filter(x => x.status !== "live");
                    otherGames.sort((a, b) => a.status.localeCompare(b.status));
                    setGames([...liveGames, ...otherGames]);
                }
            } catch (error) {
                console.error('Error fetching team data:', error);
            }
        };

        getTeam();
    }, [id]);

    if (!team) return <div className="text-center mt-12">Loading...</div>;

    const attackers = players.filter(x => x.role === "att");
    const defenders = players.filter(x => x.role === "def");

    return (
        <div className="w-[90vw] md:w-[70vw] xl:w-[52vw] mx-auto mt-12">
            {/* Team Header */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h1 className="text-4xl font-bold text-center mb-4">{team.name}</h1>
                {team.g && (
                    <div className="text-center">
                        <p className="text-xl text-gray-600">{team.g.name}</p>
                    </div>
                )}
            </div>

            {/* Attackers Section */}
            {attackers.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold mb-4">Útočníci</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="border-b-2 border-gray-300">
                                <tr>
                                    <th className="text-left py-2 px-2">#</th>
                                    <th className="text-left py-2 px-2">Meno</th>
                                    <th className="text-center py-2 px-2">G</th>
                                    <th className="text-center py-2 px-2">A</th>
                                    <th className="text-center py-2 px-2">B</th>
                                    <th className="text-center py-2 px-2">T</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attackers.map((f, i) => (
                                    <tr key={i} className="border-b border-gray-200 hover:bg-gray-50">
                                        <td className="py-3 px-2">{f.number}</td>
                                        <td className="py-3 px-2">{f.first_name} {f.last_name}</td>
                                        <td className="text-center py-3 px-2">{f.goals || 0}</td>
                                        <td className="text-center py-3 px-2">{f.assists || 0}</td>
                                        <td className="text-center py-3 px-2">{(f.goals || 0) + (f.assists || 0)}</td>
                                        <td className="text-center py-3 px-2">{f.penalties || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Defenders Section */}
            {defenders.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold mb-4">Obrancovia</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="border-b-2 border-gray-300">
                                <tr>
                                    <th className="text-left py-2 px-2">#</th>
                                    <th className="text-left py-2 px-2">Meno</th>
                                    <th className="text-center py-2 px-2">G</th>
                                    <th className="text-center py-2 px-2">A</th>
                                    <th className="text-center py-2 px-2">B</th>
                                    <th className="text-center py-2 px-2">T</th>
                                </tr>
                            </thead>
                            <tbody>
                                {defenders.map((f, i) => (
                                    <tr key={i} className="border-b border-gray-200 hover:bg-gray-50">
                                        <td className="py-3 px-2">{f.number}</td>
                                        <td className="py-3 px-2">{f.first_name} {f.last_name}</td>
                                        <td className="text-center py-3 px-2">{f.goals || 0}</td>
                                        <td className="text-center py-3 px-2">{f.assists || 0}</td>
                                        <td className="text-center py-3 px-2">{(f.goals || 0) + (f.assists || 0)}</td>
                                        <td className="text-center py-3 px-2">{f.penalties || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Keepers Section */}
            {keepers.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold mb-4">Brankári</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="border-b-2 border-gray-300">
                                <tr>
                                    <th className="text-left py-2 px-2">#</th>
                                    <th className="text-left py-2 px-2">Meno</th>
                                    <th className="text-center py-2 px-2">Z</th>
                                    <th className="text-center py-2 px-2">IG</th>
                                    <th className="text-center py-2 px-2">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {keepers.map((f, i) => {
                                    const savePercentage = (f.recieved_goals || 0) > 0
                                        ? Math.round((100 - ((f.recieved_goals || 0) / ((f.interventions || 0)) * 100)) * 100) / 100
                                        : 100;
                                    
                                    return (
                                        <tr key={i} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="py-3 px-2">{f.number}</td>
                                            <td className="py-3 px-2">{f.first_name} {f.last_name}</td>
                                            <td className="text-center py-3 px-2">{f.interventions || 0}</td>
                                            <td className="text-center py-3 px-2">{f.recieved_goals || 0}</td>
                                            <td className="text-center py-3 px-2">{savePercentage}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Games Section */}
            {games.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold mb-4">Zápasy</h2>
                    <div className="space-y-4">
                        {games.map((game, i) => (
                            <Link key={i} to={`/games/${game.ID}`}>
                                <SmallGame e={game} />
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTeam;
